import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';
import { 
  User, FileText, CreditCard, CheckCircle, Clock, AlertCircle, 
  Calendar, Phone, Mail, MapPin, Upload, Download, Eye, Trash2,
  ChevronRight, Euro, Building2, Info, ExternalLink, FileDown,
  Heart, UserPlus, AlertTriangle, Pen, Image, BookOpen, Save, FileCheck, FileSignature, Send, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PhotoIdentite } from '../components/PhotoIdentite';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { ResultatsTestsBanner } from '../components/candidat/ResultatsTestsBanner';
import { TuileLivretCandidat } from '../components/livret-accueil/TuileLivretCandidat';
import { EnregistrerDocuments } from '../components/candidat/EnregistrerDocuments';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import SignatureCanvas from '../components/SignatureCanvas';
import DossierValideCard from '../components/DossierValideCard';
import ZenButton from '../components/ZenButton';
import { paiementLabel, downloadRecu } from '../components/PaiementsCandidatCard';
import { isAdresseComplete } from '../components/fiche-candidat/AdresseCandidatCard';
import { DateInputFR } from '../components/DateInputFR';
import { PifRecapTable } from '../components/pif/PifRecapTable';
import { BoutonPhotographier } from '../components/scan/BoutonPhotographier';
import { compresserImage } from '../components/scan/scanUtils';
import { calculerAge } from '../utils/age';

// Liste des documents requis - * = obligatoire pour tests de sélection
const DOCUMENTS_REQUIS = [
  { id: 'cni_passeport', nom: 'Carte d\'identité / Passeport', obligatoire: true, description: 'Valide pour toute la durée de la formation' },
  { id: 'cv', nom: 'CV', obligatoire: true, description: 'Curriculum Vitae à jour' },
  { id: 'psc', nom: 'PSC', obligatoire: true, description: 'ou équivalent' },
  { id: 'certificat_medical', nom: 'Certificat médical', obligatoire: true, description: 'Modèle officiel FISL, moins d\'un an' },
  { id: 'photo_identite', nom: 'Photo d\'identité', obligatoire: true, description: 'Photo d\'identité récente — format JPG' },
  { id: 'attestation_natation', nom: 'Attestation de natation 100m', obligatoire: true, description: 'Modèle officiel FISL' },
  { id: 'attestation_recensement', nom: 'Attestation de recensement', obligatoire: false, description: 'Pour les moins de 25 ans' },
  { id: 'permis_cotier', nom: 'Permis côtier', obligatoire: true, description: 'Obligatoire pour les tests de sélection' },
  { id: 'permis_fluvial', nom: 'Permis fluvial', obligatoire: false, description: 'Obligatoire pour les EPMSP' },
  { id: 'crr', nom: 'CRR', obligatoire: false, description: 'Certificat restreint de radiotéléphoniste' },
  { id: 'signature', nom: 'Signature', obligatoire: true, description: 'Téléchargez votre signature ou signez via smartphone' },
];

const GENRES = [
  { value: '', label: 'Sélectionner...' },
  { value: 'M.', label: 'Monsieur' },
  { value: 'Mme', label: 'Madame' }
];

// La convention n'est visible qu'une fois envoyée par le centre
const CONVENTION_VISIBLE = ['en_attente_signature', 'signe'];

const DashboardCandidat = ({ user }) => {
  // États du profil (calqué sur stagiaire)
  const [profil, setProfil] = useState({
    // Informations personnelles
    genre: '',
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: '',
    // Adresse
    adresse: '',
    code_postal: '',
    ville: '',
    // Informations médicales
    groupe_sanguin: '',
    allergie: '',
    situation_handicap: '',
    autres_situations: '',
    // Contact d'urgence
    contact_urgence_nom: '',
    contact_urgence_prenom: '',
    contact_urgence_filiation: '',
    contact_urgence_telephone: '',
    // Questions complémentaires
    questions: {
      titulaire_bpjeps: false,
      bpjeps_lequel: '',
      bpjeps_annee: '',
      titulaire_tests_selection: false,
      date_tests_selection: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [datesTests, setDatesTests] = useState([]);
  const [tarifs, setTarifs] = useState({ tests_selection: 80, journee_preparation: 120 });
  const [paiements, setPaiements] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [photoVersion, setPhotoVersion] = useState(null);
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [testsResultat, setTestsResultat] = useState(null);
  const [prepaSaving, setPrepaSaving] = useState(false);
  const [dossierValide, setDossierValide] = useState(false);
  const [dossierEtapes, setDossierEtapes] = useState(null);
  
  // Documents supplémentaires (champs libres)
  // Documents supplémentaires (type « autre_… »), toujours synchronisés avec la liste
  const extraDocs = documents.filter(d => d.type_document?.startsWith('autre_'));
  const [showAddDocForm, setShowAddDocForm] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');
  const [dossierValidationDate, setDossierValidationDate] = useState(null);
  
  // Signature
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  
  // PIF (Plan Individuel de Formation)
  const [pifData, setPifData] = useState(null);
  const [showPifSignatureModal, setShowPifSignatureModal] = useState(false);
  const [signingPif, setSigningPif] = useState(false);
  
  // Convention de formation
  const [conventionData, setConventionData] = useState(null);
  const [showConventionSignatureModal, setShowConventionSignatureModal] = useState(false);
  const [signingConvention, setSigningConvention] = useState(false);
  const [adresseEnregistree, setAdresseEnregistree] = useState(true);

  // Devis de formation
  const [devisData, setDevisData] = useState(null);
  const [devisProspectId, setDevisProspectId] = useState(null);
  const [showDevisSignatureModal, setShowDevisSignatureModal] = useState(false);
  const [signingDevis, setSigningDevis] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [profilRes, docsRes, tarifsRes, datesRes, paiementsRes] = await Promise.all([
        axios.get('/candidats/mon-profil'),
        axios.get('/candidats/mes-documents').catch(() => ({ data: [] })),
        axios.get('/candidats/tarifs').catch(() => ({ data: { tests_selection: 80, journee_preparation: 120 } })),
        axios.get('/candidats/dates-tests').catch(() => ({ data: [] })),
        axios.get('/candidats/mes-paiements').catch(() => ({ data: [] }))
      ]);
      
      if (profilRes.data.profil) {
        setProfil(prev => ({
          ...prev,
          ...profilRes.data.profil,
          nom: profilRes.data.user?.nom || profilRes.data.nom || prev.nom,
          prenom: profilRes.data.user?.prenom || profilRes.data.prenom || prev.prenom,
          email: profilRes.data.user?.email || profilRes.data.email || prev.email,
          telephone: profilRes.data.user?.telephone || profilRes.data.telephone || prev.telephone,
          questions: profilRes.data.profil.questions || prev.questions
        }));
        setSavedSignature(profilRes.data.profil.signature);
        setAdresseEnregistree(isAdresseComplete(profilRes.data.profil));
        
        // Si pas dans le profil, charger separement
        if (!profilRes.data.profil.signature) {
          try {
            const sigRes = await axios.get('/candidats/ma-signature');
            if (sigRes.data?.signature) {
              setSavedSignature(sigRes.data.signature);
            }
          } catch (e) {}
        }
        
        // Récupérer le résultat des tests si disponible (dans profil)
        if (profilRes.data.profil.tests_selection) {
          setTestsResultat(profilRes.data.profil.tests_selection);
        }
      }
      
      // Récupérer le statut candidat depuis user
      const statutCandidat = profilRes.data.user?.statut_candidat || profilRes.data.statut_candidat;
      if (statutCandidat) {
        setProfil(prev => ({ ...prev, statut_candidat: statutCandidat }));
      }

      // Récupérer le statut de validation du dossier
      const userData = profilRes.data.user || profilRes.data;
      setDossierEtapes(userData.dossier_etapes || {});
      if (userData?.dossier_valide) {
        setDossierValide(true);
        setDossierValidationDate(userData.dossier_validation_date);
      }
      
      const docs = docsRes.data || [];
      setDocuments(docs);
      
      
      // Version de la photo d'identité (header)
      setPhotoVersion(docs.find(d => d.type_document === 'photo_identite')?.document_id || null);
      
      setTarifs(tarifsRes.data || { tests_selection: 80, journee_preparation: 120 });
      setDatesTests(datesRes.data || []);
      setPaiements(paiementsRes.data || []);
      
      // Charger le PIF si existant
      try {
        const pifRes = await axios.get('/candidats/mon-pif');
        if (pifRes.data) {
          setPifData(pifRes.data);
        }
      } catch (err) {
        // Pas de PIF, c'est normal
      }
      
      // Charger la Convention si existante
      try {
        const convRes = await axios.get('/candidats/ma-convention');
        if (CONVENTION_VISIBLE.includes(convRes.data?.statut)) {
          setConventionData(convRes.data);
        }
      } catch (err) {
        // Pas de Convention, c'est normal
      }

      // Charger le dernier devis CRM
      try {
        const meRes = await axios.get('/auth/me');
        const userId = meRes.data?.user_id;
        if (userId) {
          const devisRes = await axios.get(`/candidats/${userId}/devis`);
          if (devisRes.data?.devis && devisRes.data.devis.statut === 'en_attente_signature') {
            setDevisData(devisRes.data.devis);
            setDevisProspectId(devisRes.data.prospect_id);
          } else if (devisRes.data?.devis?.statut === 'signe') {
            setDevisData(devisRes.data.devis);
            setDevisProspectId(devisRes.data.prospect_id);
          }
        }
      } catch (err) {
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfil = async () => {
    if (!isAdresseComplete(profil)) {
      toast.error("L'adresse, le code postal et la ville sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      await axios.put('/candidats/mon-profil', profil);
      setAdresseEnregistree(true);
      toast.success('Profil enregistré avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (typeDoc, fichier) => {
    if (!fichier) return;
    let file = fichier;
    if (file.type?.startsWith('image/') && file.size > 2 * 1024 * 1024) {
      try { file = await compresserImage(file); } catch { /* fichier gardé tel quel */ }
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 2 Mo)');
      return;
    }
    
    setUploadingDoc(typeDoc);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type_document', typeDoc);
    
    try {
      await axios.post('/candidats/documents/upload', formData);
      toast.success('Document téléchargé avec succès');
      const docsRes = await axios.get('/candidats/mes-documents');
      const docs = docsRes.data || [];
      setDocuments(docs);
      
      // Si c'est la photo d'identité, mettre à jour le header
      if (typeDoc === 'photo_identite') {
        setPhotoVersion(docs.find(d => d.type_document === 'photo_identite')?.document_id || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    // Vérifier si c'est la photo
    const docToDelete = documents.find(d => d.document_id === documentId);
    const isPhoto = docToDelete?.type_document === 'photo_identite';
    
    try {
      await axios.delete(`/candidats/documents/${documentId}`);
      toast.success('Document supprimé');
      const docsRes = await axios.get('/candidats/mes-documents');
      setDocuments(docsRes.data || []);
      
      // Si c'était la photo, réinitialiser l'URL
      if (isPhoto) {
        setPhotoVersion(`supprimee-${Date.now()}`);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Aperçu dans la page (images + PDF), y compris sur iPhone
  const handleViewDocument = (documentId, nomFichier) =>
    previewDocument(documentId, nomFichier, `/candidats/doc-view/${documentId}`);

  const handleSaveSignature = async (signatureDataUrl) => {
    setSavingSignature(true);
    try {
      const formData = new FormData();
      formData.append('signature_data', signatureDataUrl);
      
      await axios.post('/candidats/signature/upload', formData);
      toast.success('Signature enregistrée');
      setShowSignatureModal(false);
      setSavedSignature(signatureDataUrl);
      
      // Recharger les documents
      const docsRes = await axios.get('/candidats/mes-documents');
      setDocuments(docsRes.data || []);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement de la signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const handlePayment = async (type) => {
    setPaymentLoading(type);
    try {
      const response = await axios.post('/candidats/paiement/creer-session', {
        type_paiement: type,
        origin_url: window.location.origin
      });
      
      // Même onglet : un nouvel onglet ouvert après chargement est bloqué sur mobile
      if (response.data.url) {
        window.location.assign(response.data.url);
        return;
      }
      setPaymentLoading(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setPaymentLoading(null);
    }
  };

  const handleDownloadAttestation = async () => {
    try {
      const response = await axios.get(`/candidats/${user.user_id}/attestation/auto`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attestation_selection_${profil.nom || ''}_${profil.prenom || ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Impossible de télécharger l'attestation");
    }
  };

  const handleDownloadLettreReussite = async () => {
    try {
      const response = await axios.get('/candidats/lettre-reussite/pdf', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${profil.nom}_${profil.prenom}_lettre_reussite.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Lettre de réussite téléchargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  const handleDownloadPIF = async () => {
    try {
      const response = await axios.get('/candidats/mon-pif/pdf', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${profil.nom}_${profil.prenom}_PIF.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plan de Formation téléchargé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  const handleSignPIF = async (signatureDataUrl) => {
    setSigningPif(true);
    try {
      const formData = new FormData();
      formData.append('signature_data', signatureDataUrl);
      
      await axios.post('/candidats/pif/signer', formData);
      toast.success('Plan Individuel de Formation signé avec succès !');
      setShowPifSignatureModal(false);
      
      // Sauvegarder la signature dans le profil pour reutilisation
      setSavedSignature(signatureDataUrl);
      try {
        const sigFormData = new FormData();
        sigFormData.append('signature_data', signatureDataUrl);
        await axios.post('/candidats/signature/upload', sigFormData);
      } catch (e) { /* ignore - non bloquant */ }
      
      // Recharger les données
      const pifRes = await axios.get('/candidats/mon-pif');
      if (pifRes.data) {
        setPifData(pifRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigningPif(false);
    }
  };

  // Convention de formation
  const handleDownloadConvention = async () => {
    try {
      const response = await axios.get('/candidats/ma-convention/pdf', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${profil.nom}_${profil.prenom}_convention.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Convention téléchargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  const handleSignConvention = async (signatureDataUrl) => {
    setSigningConvention(true);
    try {
      await axios.post('/candidats/convention/signer', { signature: signatureDataUrl });
      toast.success('Convention signée avec succès ! Vous êtes inscrit en formation.');
      setShowConventionSignatureModal(false);
      
      // Sauvegarder la signature dans le profil pour reutilisation
      setSavedSignature(signatureDataUrl);
      try {
        const sigFormData = new FormData();
        sigFormData.append('signature_data', signatureDataUrl);
        await axios.post('/candidats/signature/upload', sigFormData);
      } catch (e) { /* ignore - non bloquant */ }
      
      // Recharger les données
      const convRes = await axios.get('/candidats/ma-convention');
      if (CONVENTION_VISIBLE.includes(convRes.data?.statut)) {
        setConventionData(convRes.data);
      }
      // Recharger aussi le profil pour avoir le nouveau statut
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigningConvention(false);
    }
  };

  // === Devis handlers ===
  const handleDownloadDevis = async () => {
    if (!devisData || !devisProspectId) return;
    try {
      const response = await axios.get(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openBlobInNewTab(blob);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleSignDevis = async (signatureDataUrl) => {
    setSigningDevis(true);
    try {
      await axios.post('/candidats/devis/signer', { signature: signatureDataUrl });
      toast.success('Devis signé avec succès !');
      setShowDevisSignatureModal(false);
      
      // Sauvegarder la signature dans le profil pour reutilisation
      setSavedSignature(signatureDataUrl);
      try {
        const sigFormData = new FormData();
        sigFormData.append('signature_data', signatureDataUrl);
        await axios.post('/candidats/signature/upload', sigFormData);
      } catch (e) { /* ignore - non bloquant */ }
      
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigningDevis(false);
    }
  };

  const getDocumentStatus = (docId) => {
    const doc = documents.find(d => d.type_document === docId);
    if (!doc) return null;
    return doc;
  };

  // Calculer les statistiques
  const documentsUploaded = DOCUMENTS_REQUIS.filter(d => getDocumentStatus(d.id)).length;
  const documentsRequired = DOCUMENTS_REQUIS.filter(d => d.obligatoire).length;
  const hasPayedTests = paiements.some(p => p.type === 'tests_selection' && p.statut === 'completed');
  const hasPayedPrepa = paiements.some(p => p.type === 'journee_preparation' && p.statut === 'completed');
  const choisirPrepa = async (v) => {
    setPrepaSaving(true);
    try {
      await axios.put('/candidats/ma-prepa', { fait_prepa: v });
      setProfil(prev => ({ ...prev, fait_prepa: v }));
      toast.success(v ? 'Inscription à la préparation enregistrée' : 'Inscription à la préparation annulée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setPrepaSaving(false);
    }
  };
  const montantPaye = (type) => paiements.filter(p => p.type === type && p.statut === 'completed').reduce((s, p) => s + Number(p.montant || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-candidat">
      <ZenButton />
      {dossierEtapes && Object.keys(dossierEtapes).length > 0 && (() => {
        const etapes = dossierEtapes || {};
        const steps = [
          { key: 'infos_administratives', label: 'Informations administratives' },
          { key: 'pieces_fournir', label: 'Pièces à fournir' },
          { key: 'paiement_frais', label: 'Paiement des frais' },
        ];
        const nbValides = steps.filter(s => etapes[s.key]).length;
        const pct = Math.round((nbValides / steps.length) * 100);
        const isComplete = nbValides === steps.length;
        const docsRefuses = documents.filter(d => d.statut === 'refuse');

        return (
          <div className={`p-4 rounded-lg border-l-4 ${isComplete ? 'bg-green-50 border-green-500' : docsRefuses.length > 0 ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`} data-testid="dossier-progress">
            <div className="flex items-center gap-3 mb-2">
              {isComplete ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : docsRefuses.length > 0 ? (
                <AlertCircle className="h-6 w-6 text-red-500" />
              ) : (
                <Clock className="h-6 w-6 text-amber-500" />
              )}
              <div className="flex-1">
                <h3 className={`font-bold ${isComplete ? 'text-green-800' : docsRefuses.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                  {isComplete ? 'Dossier validé' : 'Dossier incomplet'}
                </h3>
                <p className="text-sm text-slate-600">{nbValides} / {steps.length} étapes validées par le centre</p>
              </div>
              <span className={`text-2xl font-bold ${isComplete ? 'text-green-600' : 'text-amber-600'}`}>{pct}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : docsRefuses.length > 0 ? 'bg-red-400' : 'bg-amber-400'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {isComplete && (
              <div className="mt-3 p-3 bg-green-100 rounded-lg text-sm text-green-800">
                <p className="font-medium mb-2">Vous recevrez une convocation aux tests de sélection quinze jours avant la date prévue.</p>
                <p className="mb-1">En attendant, veuillez penser à vous équiper :</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>une combinaison néoprène de 2 mm,</li>
                  <li>un gilet de sauvetage 50 N,</li>
                  <li>un dispositif lumineux,</li>
                  <li>une paire de chaussures.</li>
                </ul>
              </div>
            )}
            {!isComplete && (
              <div className="mt-3 space-y-1">
                {steps.map(s => (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    {etapes[s.key] ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-slate-400" />
                    )}
                    <span className={etapes[s.key] ? 'text-green-700' : 'text-slate-500'}>{s.label}</span>
                  </div>
                ))}
              </div>
            )}
            {docsRefuses.length > 0 && (
              <p className="text-xs text-red-600 mt-2">
                {docsRefuses.length} document(s) refusé(s) — veuillez les corriger et re-uploader.
              </p>
            )}
          </div>
        );
      })()}

      {/* Header Bandeau */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#6BA82A] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Photo */}
          <div className="flex-shrink-0">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden flex items-center justify-center">
              <PhotoIdentite
                userId={user?.user_id}
                version={photoVersion}
                className="w-full h-full object-cover"
                testId="candidat-header-photo"
                fallback={<span className="text-3xl font-bold">{profil.prenom?.[0]}{profil.nom?.[0]}</span>}
              />
            </div>
          </div>
          
          {/* Infos */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-white/20">{profil.genre || 'Genre'}</Badge>
              <Badge className="bg-amber-500">Candidat</Badge>
              {profil.statut_candidat === 'accepte' && <Badge className="bg-green-500">Accepté</Badge>}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{profil.prenom} {profil.nom}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/80 mt-2">
              <span className="flex items-center gap-1">
                <Mail className="h-4 w-4" /> {profil.email}
              </span>
              {profil.telephone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-4 w-4" /> {profil.telephone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" /> FISL {user?.centre === 'occitanie' ? 'Occitanie' : 'Atlantique'}
              </span>
            </div>
          </div>
          
          {/* Stats rapides */}
          <div className="flex flex-wrap gap-3">
            <div className="bg-white/10 rounded-lg px-4 py-2 text-center">
              <p className="text-2xl font-bold">{documentsUploaded}/{DOCUMENTS_REQUIS.length}</p>
              <p className="text-xs text-white/70">Documents</p>
            </div>
            <div className={`rounded-lg px-4 py-2 text-center ${hasPayedTests ? 'bg-green-500/30' : 'bg-white/10'}`}>
              <p className="text-lg font-bold">{hasPayedTests ? '✓' : '—'}</p>
              <p className="text-xs text-white/70">Tests payés</p>
            </div>
          </div>
        </div>
      </div>

      <TuileLivretCandidat />

      <ResultatsTestsBanner
        resultat={testsResultat}
        statut={profil.statut_candidat}
        lettre={{ onTelecharger: handleDownloadLettreReussite, onVoir: () => previewDocument(null, 'Lettre de réussite.pdf', '/candidats/lettre-reussite/pdf') }}
        attestation={{ onTelecharger: handleDownloadAttestation, onVoir: () => previewDocument(null, 'Attestation de sélection.pdf', `/candidats/${user.user_id}/attestation/auto`) }}
      />

      {/* Section PIF (Plan Individuel de Formation) - Visible uniquement si créé */}
      {pifData && (
      <Card className="border-l-4 border-blue-500 shadow-md" data-testid="pif-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className={`hidden sm:block p-3 rounded-full ${
              pifData?.statut === 'signe' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              <BookOpen className={`h-6 w-6 ${
                pifData?.statut === 'signe' ? 'text-green-600' : 'text-blue-600'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-800">
                  Plan Individuel de Formation (PIF)
                </h3>
                <Badge className={pifData.statut === 'signe' ? 'bg-green-500' : 'bg-amber-500'}>
                  {pifData.statut === 'signe' ? 'Signé' : 'En attente de signature'}
                </Badge>
              </div>
              
              {pifData.statut === 'signe' ? (
                <p className="text-sm text-green-700 mb-3">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  Signé le {pifData.date_signature} - Bon pour accord
                </p>
              ) : (
                <p className="text-sm text-amber-700 mb-3">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Veuillez consulter et signer votre Plan Individuel de Formation.
                </p>
              )}
              
              {/* Récapitulatif des heures */}
              <div className="mb-4">
                <PifRecapTable pif={pifData} stage={pifData.total_heures_stage ?? 496} testPrefix="mon-pif" />
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPIF}
                  className="border-blue-300"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger le PDF
                </Button>
                <Button variant="outline" size="sm" className="border-blue-300" onClick={() => previewDocument(null, 'PIF.pdf', '/candidats/mon-pif/pdf')} data-testid="btn-voir-pif">
                  <Eye className="h-4 w-4 mr-2" />
                  Voir
                </Button>
                {pifData.statut !== 'signe' && (
                  <Button
                    onClick={() => setShowPifSignatureModal(true)}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Pen className="h-4 w-4 mr-2" />
                    Signer mon PIF
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section Devis - Visible si envoyé pour signature */}
      {devisData && (
      <Card className="border-l-4 border-teal-500 shadow-md" data-testid="devis-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${
              devisData?.statut === 'signe' ? 'bg-green-100' : 'bg-teal-100'
            }`}>
              <FileText className={`h-6 w-6 ${
                devisData?.statut === 'signe' ? 'text-green-600' : 'text-teal-600'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-800">
                  Devis de formation
                </h3>
                <Badge className={devisData.statut === 'signe' ? 'bg-green-500' : 'bg-amber-500'}>
                  {devisData.statut === 'signe' ? 'Signé' : 'En attente de signature'}
                </Badge>
              </div>
              
              {devisData.statut === 'signe' ? (
                <p className="text-sm text-green-700 mb-3">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  Signé le {devisData.date_signature} - Devis accepté
                </p>
              ) : (
                <p className="text-sm text-amber-700 mb-3">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Veuillez consulter et signer votre devis de formation.
                </p>
              )}
              
              <div className="bg-teal-50 rounded-lg p-3 mb-3 text-sm space-y-1">
                <p><span className="text-slate-500">N° Devis :</span> <span className="font-medium">{devisData.devis_numero || devisData.devis_id}</span></p>
                <p><span className="text-slate-500">Montant total :</span> <span className="font-semibold text-teal-700">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devisData.montant_total || 0)}</span></p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadDevis}
                  className="border-teal-300"
                  data-testid="btn-download-devis-candidat"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger le devis
                </Button>
                <Button variant="outline" size="sm" className="border-teal-300" onClick={() => previewDocument(null, 'Devis.pdf', `/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`)} data-testid="btn-voir-devis">
                  <Eye className="h-4 w-4 mr-2" />
                  Voir
                </Button>
                {devisData.statut !== 'signe' && (
                  <Button
                    onClick={() => setShowDevisSignatureModal(true)}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="btn-sign-devis"
                  >
                    <Pen className="h-4 w-4 mr-2" />
                    Signer mon devis
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section Convention de formation - Visible uniquement si créée */}
      {conventionData && (
      <Card className="border-l-4 border-blue-500 shadow-md" data-testid="convention-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${
              conventionData?.statut === 'signe' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              <FileSignature className={`h-6 w-6 ${
                conventionData?.statut === 'signe' ? 'text-green-600' : 'text-blue-600'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-800">
                  Convention de formation
                </h3>
                <Badge className={conventionData.statut === 'signe' ? 'bg-green-500' : 'bg-amber-500'}>
                  {conventionData.statut === 'signe' ? 'Signée' : 'En attente de signature'}
                </Badge>
              </div>
              
              {conventionData.statut === 'signe' ? (
                <p className="text-sm text-green-700 mb-4">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  Signée le {conventionData.date_signature} - Vous êtes inscrit(e) en formation !
                </p>
              ) : (
                <p className="text-sm text-amber-700 mb-4">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Veuillez signer cette convention pour confirmer votre entrée en formation.
                </p>
              )}
              
              {conventionData.statut !== 'signe' && !adresseEnregistree && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="convention-adresse-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Votre adresse est incomplète. Renseignez votre adresse, code postal et ville ci-dessous puis cliquez sur « Enregistrer le dossier » avant de signer votre convention.</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadConvention}
                  className="border-blue-300"
                  data-testid="btn-download-convention"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger la convention
                </Button>
                <Button variant="outline" size="sm" className="border-blue-300" onClick={() => previewDocument(null, 'Convention.pdf', '/candidats/ma-convention/pdf')} data-testid="btn-voir-convention">
                  <Eye className="h-4 w-4 mr-2" />
                  Voir
                </Button>
                {conventionData.statut !== 'signe' && (
                  <Button
                    onClick={() => setShowConventionSignatureModal(true)}
                    disabled={!adresseEnregistree}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-sign-convention"
                  >
                    <Pen className="h-4 w-4 mr-2" />
                    Signer ma convention
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section 1: Informations Personnelles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-[#8DC63F]" />
            Informations Personnelles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Genre *</Label>
              <select
                value={profil.genre || ''}
                onChange={(e) => setProfil({ ...profil, genre: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
                data-testid="select-genre"
              >
                {GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Prénom *</Label>
              <Input value={profil.prenom} onChange={(e) => setProfil({ ...profil, prenom: e.target.value })} />
            </div>
            
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={profil.nom} onChange={(e) => setProfil({ ...profil, nom: e.target.value })} />
            </div>
            
            <div className="space-y-2">
              <Label>
                Date de naissance *
                {calculerAge(profil.date_naissance) !== null && (
                  <span className="ml-2 text-[#8DC63F] font-semibold" data-testid="profil-age">({calculerAge(profil.date_naissance)} ans)</span>
                )}
              </Label>
              <DateInputFR
                value={profil.date_naissance || ''}
                onChange={(iso) => setProfil(prev => ({ ...prev, date_naissance: iso }))}
                data-testid="profil-date-naissance-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Lieu de naissance *</Label>
              <Input 
                value={profil.lieu_naissance || ''} 
                onChange={(e) => setProfil({ ...profil, lieu_naissance: e.target.value })}
                placeholder="Ville de naissance" 
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nationalité</Label>
              <Input 
                value={profil.nationalite || ''} 
                onChange={(e) => setProfil({ ...profil, nationalite: e.target.value })}
                placeholder="Française" 
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={profil.email} onChange={(e) => setProfil({ ...profil, email: e.target.value })} className="pl-10" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Téléphone *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={profil.telephone || ''} onChange={(e) => setProfil({ ...profil, telephone: e.target.value })} className="pl-10" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Adresse */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#8DC63F]" />
            Adresse
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-3">
              <Label>Adresse (numéro et rue) *</Label>
              <Input 
                value={profil.adresse || ''} 
                onChange={(e) => setProfil({ ...profil, adresse: e.target.value })}
                placeholder="123 rue de la Formation" 
                data-testid="profil-adresse-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Code Postal *</Label>
              <Input 
                value={profil.code_postal || ''} 
                onChange={(e) => setProfil({ ...profil, code_postal: e.target.value })}
                placeholder="17110"
                maxLength={5} 
                data-testid="profil-code-postal-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Ville *</Label>
              <Input 
                value={profil.ville || ''} 
                onChange={(e) => setProfil({ ...profil, ville: e.target.value })}
                placeholder="Saint-Georges-de-Didonne" 
                data-testid="profil-ville-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Informations Médicales */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-[#8DC63F]" />
            Informations Médicales
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Groupe Sanguin</Label>
              <select
                value={profil.groupe_sanguin || ''}
                onChange={(e) => setProfil({ ...profil, groupe_sanguin: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
              >
                <option value="">Sélectionner...</option>
                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Allergies</Label>
              <Input 
                value={profil.allergie || ''} 
                onChange={(e) => setProfil({ ...profil, allergie: e.target.value })}
                placeholder="Aucune ou préciser" 
              />
            </div>
            <div className="space-y-2">
              <Label>Situation de Handicap</Label>
              <Input 
                value={profil.situation_handicap || ''} 
                onChange={(e) => setProfil({ ...profil, situation_handicap: e.target.value })}
                placeholder="Aucune ou préciser" 
              />
            </div>
            <div className="space-y-2">
              <Label>Autres Situations Particulières</Label>
              <Input 
                value={profil.autres_situations || ''} 
                onChange={(e) => setProfil({ ...profil, autres_situations: e.target.value })} 
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Contact d'Urgence */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#8DC63F]" />
            Contact d'Urgence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input 
                value={profil.contact_urgence_nom || ''} 
                onChange={(e) => setProfil({ ...profil, contact_urgence_nom: e.target.value })} 
              />
            </div>
            <div className="space-y-2">
              <Label>Prénom</Label>
              <Input 
                value={profil.contact_urgence_prenom || ''} 
                onChange={(e) => setProfil({ ...profil, contact_urgence_prenom: e.target.value })} 
              />
            </div>
            <div className="space-y-2">
              <Label>Lien de parenté</Label>
              <select
                value={profil.contact_urgence_filiation || ''}
                onChange={(e) => setProfil({ ...profil, contact_urgence_filiation: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
              >
                <option value="">Sélectionner...</option>
                {['Père', 'Mère', 'Conjoint(e)', 'Frère', 'Soeur', 'Ami(e)', 'Autre'].map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  value={profil.contact_urgence_telephone || ''} 
                  onChange={(e) => setProfil({ ...profil, contact_urgence_telephone: e.target.value })}
                  className="pl-10" 
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Questions complémentaires (Inscription Centre) */}
      <Card className="border-2 border-[#8DC63F]/30">
        <CardHeader className="bg-[#8DC63F]/5">
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-[#8DC63F]" />
            Informations complémentaires
          </CardTitle>
          <CardDescription>Ces informations sont requises pour votre inscription aux tests de sélection</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* BPJEPS */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="titulaire_bpjeps"
                checked={profil.questions?.titulaire_bpjeps || false}
                onChange={(e) => setProfil({
                  ...profil,
                  questions: { ...profil.questions, titulaire_bpjeps: e.target.checked }
                })}
                className="w-5 h-5 text-[#8DC63F] rounded"
              />
              <Label htmlFor="titulaire_bpjeps" className="font-medium cursor-pointer">
                Êtes-vous déjà titulaire d'un BPJEPS ?
              </Label>
            </div>
            
            {profil.questions?.titulaire_bpjeps && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8 p-4 bg-slate-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Lequel ?</Label>
                  <Input
                    value={profil.questions?.bpjeps_lequel || ''}
                    onChange={(e) => setProfil({
                      ...profil,
                      questions: { ...profil.questions, bpjeps_lequel: e.target.value }
                    })}
                    placeholder="Ex: APT, AF, AAN..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Année d'obtention</Label>
                  <Input
                    value={profil.questions?.bpjeps_annee || ''}
                    onChange={(e) => setProfil({
                      ...profil,
                      questions: { ...profil.questions, bpjeps_annee: e.target.value }
                    })}
                    placeholder="2023"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Tests de sélection */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="titulaire_tests"
                checked={profil.questions?.titulaire_tests_selection || false}
                onChange={(e) => setProfil({
                  ...profil,
                  questions: { ...profil.questions, titulaire_tests_selection: e.target.checked }
                })}
                className="w-5 h-5 text-[#8DC63F] rounded"
              />
              <Label htmlFor="titulaire_tests" className="font-medium cursor-pointer">
                Êtes-vous déjà titulaire des tests de sélection du BPJEPS Motonautisme ?
              </Label>
            </div>
          </div>

          {/* Date tests souhaitée */}
          <div className="space-y-2">
            <Label className="font-medium">Date souhaitée pour passer les tests de sélection</Label>
            <select
              value={profil.questions?.date_tests_selection || ''}
              onChange={(e) => setProfil({
                ...profil,
                questions: { ...profil.questions, date_tests_selection: e.target.value }
              })}
              className="w-full md:w-1/2 h-10 px-3 text-sm border border-slate-200 rounded-md"
            >
              <option value="">Sélectionner une date...</option>
              {datesTests.length > 0 ? (
                datesTests.map((d, i) => (
                  <option key={i} value={d.date}>
                    {d.label || new Date(d.date).toLocaleDateString('fr-FR')}
                  </option>
                ))
              ) : (
                <option value="" disabled>Aucune date disponible</option>
              )}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Bouton Enregistrer le dossier */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSaveProfil} 
          disabled={saving}
          className="bg-[#8DC63F] hover:bg-[#7db336]"
          data-testid="btn-save-profil"
        >
          {saving ? (
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Enregistrer le dossier
        </Button>
      </div>

      {/* Section 6: Documents */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#8DC63F]" />
                Compléter et uploader vos documents
              </CardTitle>
              <CardDescription className="mt-1">
                <span className="text-red-500">*</span> Documents obligatoires pour les tests de sélection
              </CardDescription>
            </div>
            <Badge variant="outline">{documentsUploaded} / {DOCUMENTS_REQUIS.length} documents</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Lien tutoriel */}
          <a 
            href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/candidats/notice-documents`}
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="tutoriel-documents-link"
            className="flex items-center gap-3 p-4 mb-6 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-[#8DC63F] rounded-lg flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-700">Tutoriel pour l'envoi des documents</p>
              <p className="text-sm text-slate-500">Cliquez ici pour télécharger le guide de préparation</p>
            </div>
            <Download className="h-5 w-5 text-slate-400" />
          </a>

          <p className="text-sm text-slate-500 mb-2">
            Fichiers acceptés : PDF ou JPG, taille max 2 Mo
          </p>
          <p className="flex items-start gap-2 text-sm text-slate-600 mb-4 p-3 rounded-lg bg-[#8DC63F]/10 border border-[#8DC63F]/30" data-testid="aide-photographier">
            <Camera className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#5a8a1f]" />
            <span>Sur smartphone, touchez <strong>Photographier</strong> : l'appli recadre automatiquement le document (sans la table autour), retire les ombres et en fait un scan PDF A4 conforme (recto et verso réunis sur une page pour les cartes). Pour la photo d'identité : selfie recadré au format 35 × 45 mm.</span>
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DOCUMENTS_REQUIS.map((doc) => {
              const uploadedDoc = getDocumentStatus(doc.id);
              const isSignature = doc.id === 'signature';
              
              return (
                <div
                  key={doc.id}
                  data-testid={`doc-row-${doc.id}`}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border ${
                    uploadedDoc 
                      ? uploadedDoc.statut === 'refuse' 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-green-50 border-green-200'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {uploadedDoc ? (
                      uploadedDoc.statut === 'refuse' ? (
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      )
                    ) : isSignature ? (
                      <Pen className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <span className="text-sm text-slate-700 font-medium">
                        {doc.nom} {doc.obligatoire && <span className="text-red-500">*</span>}
                      </span>
                      <p className="text-xs text-slate-500 truncate">{doc.description}</p>
                      {uploadedDoc?.statut === 'refuse' && (
                        <p className="text-xs text-red-600 font-medium mt-1">
                          Refusé : {uploadedDoc.motif_refus || 'Document non conforme'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                    {uploadedDoc && uploadedDoc.statut !== 'refuse' ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDocument(uploadedDoc.document_id, uploadedDoc.nom_fichier)}
                          title="Voir le document"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(uploadedDoc.document_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : isSignature ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10"
                          onClick={() => setShowSignatureModal(true)}
                          data-testid="btn-signer"
                        >
                          <Pen className="h-3 w-3 mr-1" />
                          Signer
                        </Button>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => handleUploadDocument(doc.id, e.target.files[0])}
                            disabled={uploadingDoc === doc.id}
                          />
                          <Button size="sm" variant="outline" className="text-xs" disabled={uploadingDoc === doc.id}>
                            <Image className="h-3 w-3 mr-1" />
                            Image
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                      <BoutonPhotographier doc={doc} onFichier={(f) => handleUploadDocument(doc.id, f)} disabled={uploadingDoc === doc.id} />
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) => handleUploadDocument(doc.id, e.target.files[0])}
                          disabled={uploadingDoc === doc.id}
                        />
                        <Button size="sm" variant="outline" className="text-xs" disabled={uploadingDoc === doc.id}>
                          {uploadingDoc === doc.id ? (
                            <div className="animate-spin h-3 w-3 border-2 border-slate-500 border-t-transparent rounded-full" />
                          ) : (
                            <>
                              <Upload className="h-3 w-3 mr-1" />
                              {uploadedDoc?.statut === 'refuse' ? 'Renvoyer' : 'Ajouter'}
                            </>
                          )}
                        </Button>
                      </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Documents supplémentaires */}
          {extraDocs.filter(d => !DOCUMENTS_REQUIS.some(r => r.id === d.type_document)).length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-slate-600">Documents supplémentaires :</p>
              {extraDocs.filter(d => !DOCUMENTS_REQUIS.some(r => r.id === d.type_document)).map((doc) => (
                <div key={doc.document_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm text-slate-700 font-medium">{doc.type_document?.replace('autre_', '').replace(/_/g, ' ')}</span>
                      <p className="text-xs text-slate-500 truncate">{doc.nom_fichier}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                    <Button size="sm" variant="ghost" onClick={() => handleViewDocument(doc.document_id, doc.nom_fichier)} title="Voir">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteDocument(doc.document_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ajouter un document supplémentaire */}
          <div className="mt-4">
            {showAddDocForm ? (
              <div className="p-4 border border-dashed border-[#8DC63F] rounded-lg bg-green-50/50 space-y-3">
                <Label className="text-sm font-medium">Nom du document :</Label>
                <Input
                  value={newDocLabel}
                  onChange={(e) => setNewDocLabel(e.target.value)}
                  placeholder="Ex: Attestation employeur, Diplôme..."
                  data-testid="input-extra-doc-label"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => {
                        if (!newDocLabel.trim()) {
                          toast.error('Veuillez saisir un nom pour le document');
                          return;
                        }
                        const typeId = 'autre_' + newDocLabel.trim().toLowerCase().replace(/\s+/g, '_');
                        handleUploadDocument(typeId, e.target.files[0]);
                        setShowAddDocForm(false);
                        setNewDocLabel('');
                      }}
                      disabled={!newDocLabel.trim()}
                    />
                    <Button size="sm" className="w-full bg-[#8DC63F] hover:bg-[#7db336]" disabled={!newDocLabel.trim()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Choisir le fichier
                    </Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setShowAddDocForm(false); setNewDocLabel(''); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full border-dashed border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10"
                onClick={() => setShowAddDocForm(true)}
                data-testid="btn-add-extra-doc"
              >
                <Upload className="h-4 w-4 mr-2" />
                + Ajouter un document supplémentaire
              </Button>
            )}
          </div>

          <EnregistrerDocuments
            nbEnAttente={documents.filter(d => d.envoye_centre === false).length}
            endpoint="/candidats/documents/enregistrer"
            onEnvoye={async () => setDocuments((await axios.get('/candidats/mes-documents')).data || [])}
          />
        </CardContent>
      </Card>
      {!hasPayedTests && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg flex items-start gap-3" data-testid="paiement-warning">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 font-medium">
            Votre candidature sera prise en compte uniquement après paiement des frais d'inscription.
          </p>
        </div>
      )}

      <Card className="border-2 border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-600" />
            Régler les frais d'inscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tests de sélection */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${hasPayedTests ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3">
              {hasPayedTests ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <Clock className="h-6 w-6 text-amber-500" />
              )}
              <div>
                <h4 className="font-semibold text-slate-800">Tests de sélection</h4>
                <p className="text-sm text-slate-500">
                  {hasPayedTests ? 'Paiement effectué' : 'En attente de paiement'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-bold text-slate-800">{hasPayedTests ? montantPaye('tests_selection') : tarifs.tests_selection}€</span>
              {!hasPayedTests && (
                <Button
                  onClick={() => handlePayment('tests_selection')}
                  disabled={paymentLoading === 'tests_selection'}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="btn-payer-tests"
                >
                  {paymentLoading === 'tests_selection' ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Payer
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Journée de préparation */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${hasPayedPrepa ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3">
              {hasPayedPrepa ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <Clock className="h-6 w-6 text-slate-400" />
              )}
              <div>
                <h4 className="font-semibold text-slate-800">Journée de préparation</h4>
                <p className={`text-sm ${!hasPayedPrepa && profil.fait_prepa ? 'text-amber-700 font-medium' : 'text-slate-500'}`} data-testid="prepa-statut">
                  {hasPayedPrepa ? 'Paiement effectué' : profil.fait_prepa ? 'Vous êtes inscrit · paiement à régler (en ligne ou sur place)' : 'Optionnel - Préparation aux tests'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <span className="text-2xl font-bold text-slate-800">{hasPayedPrepa ? montantPaye('journee_preparation') : tarifs.journee_preparation}€</span>
              {!hasPayedPrepa && (
                <Button variant="ghost" size="sm" onClick={() => choisirPrepa(!profil.fait_prepa)} disabled={prepaSaving}
                  className={profil.fait_prepa ? 'text-slate-500' : 'text-[#5d8a22] border border-[#8DC63F]'} data-testid="btn-prepa-plus-tard">
                  {profil.fait_prepa ? 'Annuler mon inscription' : "Je m'inscris, je paierai plus tard"}
                </Button>
              )}
              {!hasPayedPrepa && (
                <Button
                  onClick={() => handlePayment('journee_preparation')}
                  disabled={paymentLoading === 'journee_preparation'}
                  variant="outline"
                  data-testid="btn-payer-prepa"
                >
                  {paymentLoading === 'journee_preparation' ? (
                    <div className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {profil.fait_prepa ? 'Payer en ligne' : "S'inscrire et payer"}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {paiements.length > 0 && (
            <div className="pt-2 border-t border-amber-200" data-testid="candidat-paiements-list">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Vos paiements enregistrés</h4>
              <ul className="space-y-1.5">
                {paiements.map((p, i) => (
                  <li key={p.transaction_id || i} className="flex justify-between items-center gap-3 text-sm text-slate-600">
                    <span>{paiementLabel(p)} · {p.date ? new Date(p.date).toLocaleDateString('fr-FR') : ''}{p.mode_label ? ` · ${p.mode_label}` : ''}</span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="font-semibold text-slate-800">{Number(p.montant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                      {p.transaction_id && (
                        <button
                          onClick={() => downloadRecu(`/candidats/mes-paiements/${p.transaction_id}/recu`, p)}
                          className="flex items-center gap-1 text-xs font-medium text-[#5d8a22] hover:underline"
                          data-testid={`candidat-recu-${p.transaction_id}`}
                        >
                          <FileDown className="h-3.5 w-3.5" /> Reçu
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Informations Utiles */}
      <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="infos-utiles-card">
        <CardHeader className="bg-blue-100/50">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Info className="h-5 w-5" />
            Informations utiles
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-5">
          {/* Adresse du centre */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-600" />
              Adresse du centre
            </h4>
            <a
              href="https://www.google.com/maps/search/?api=1&query=4+bis+rue+Amp%C3%A8re+17110+Saint-Georges-de-Didonne"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors group"
              data-testid="link-adresse-centre"
            >
              <div>
                <p className="font-medium text-slate-800">4 bis rue Ampère</p>
                <p className="text-sm text-slate-600">17110 Saint-Georges-de-Didonne</p>
              </div>
              <ExternalLink className="h-4 w-4 text-blue-500 group-hover:text-blue-700 ml-2" />
            </a>
          </div>

          {/* Hébergement */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              Hébergement pendant la formation
            </h4>
            <div className="space-y-2">
              <p className="text-sm text-slate-600 font-medium">Logements saisonniers :</p>
              <div className="flex flex-col gap-2">
                <a
                  href="https://www.agglo-royan.fr/logement-saisonnier"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                  data-testid="link-cara"
                >
                  <span className="font-medium text-slate-800">CARA</span>
                  <span className="text-slate-400">—</span>
                  <span className="text-blue-600 underline">www.agglo-royan.fr/logement-saisonnier</span>
                  <ExternalLink className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                </a>
                <a
                  href="https://www.ml-pays-royannais.com/logement-saisonnier"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                  data-testid="link-mission-locale"
                >
                  <span className="font-medium text-slate-800">Mission Locale</span>
                  <span className="text-slate-400">—</span>
                  <span className="text-blue-600 underline">www.ml-pays-royannais.com</span>
                  <ExternalLink className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                </a>
                <div className="ml-1 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="mission-locale-info">
                  <p className="font-semibold text-amber-800 mb-1">Mission Locale (pour les moins de 25 ans) :</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    <li>Solutions d'hébergement</li>
                    <li>Aide aux logements possible</li>
                    <li>Services mobilité (prêts de vélos)</li>
                  </ul>
                </div>
                <a
                  href="https://www.leboncoin.fr/recherche?category=10&locations=Saint-Georges-de-Didonne__17110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                  data-testid="link-leboncoin"
                >
                  <span className="font-medium text-slate-800">Le Bon Coin</span>
                  <span className="text-slate-400">—</span>
                  <span className="text-blue-600 underline">Rechercher un logement à proximité</span>
                  <ExternalLink className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Signature */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="h-5 w-5 text-[#8DC63F]" />
              Signer electroniquement
            </DialogTitle>
            <DialogDescription>
              Dessinez votre signature ci-dessous. Elle sera enregistree pour etre reutilisee lors de la signature de documents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <SignatureCanvas
              onSave={handleSaveSignature}
              onCancel={() => setShowSignatureModal(false)}
              loading={savingSignature}
              existingSignature={savedSignature}
              width={400}
              height={200}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Signature PIF */}
      <Dialog open={showPifSignatureModal} onOpenChange={setShowPifSignatureModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Signer mon Plan Individuel de Formation
            </DialogTitle>
            <DialogDescription>
              En signant ce document, vous acceptez les termes de votre Plan Individuel de Formation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <SignatureCanvas
              onSave={handleSignPIF}
              onCancel={() => setShowPifSignatureModal(false)}
              loading={signingPif}
              existingSignature={savedSignature}
              width={400}
              height={200}
              buttonText="Signer le PIF"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Signature Convention */}
      <Dialog open={showConventionSignatureModal} onOpenChange={setShowConventionSignatureModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-blue-600" />
              Signer ma Convention de formation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              En signant cette convention, vous confirmez votre entree en formation. 
              Veuillez d'abord telecharger et lire attentivement la convention avant de signer.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Votre signature :</h4>
              <SignatureCanvas
                onSave={handleSignConvention}
                loading={signingConvention}
                existingSignature={savedSignature}
                buttonText="Signer la convention"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDevisSignatureModal} onOpenChange={setShowDevisSignatureModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-teal-600" />
              Signer mon Devis de formation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              En signant ce devis, vous acceptez les conditions tarifaires de votre formation.
              Veuillez d'abord telecharger et lire attentivement le devis avant de signer.
            </p>
            {devisData && (
              <div className="bg-teal-50 rounded-lg p-3 text-sm">
                <p><span className="text-slate-500">Montant :</span> <span className="font-semibold text-teal-700">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devisData.montant_total || 0)}</span></p>
              </div>
            )}
            <div className="p-4 bg-teal-50 rounded-lg">
              <h4 className="font-semibold text-teal-800 mb-2">Votre signature :</h4>
              <SignatureCanvas
                onSave={handleSignDevis}
                loading={signingDevis}
                existingSignature={savedSignature}
                buttonText="Signer le devis"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aperçu des documents (images + PDF) */}
      <DocumentModal />
    </div>
  );
};

export default DashboardCandidat;
